import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, catchError, map, of, tap, timeout } from 'rxjs';
import { API, environment } from '@core/envs';

import { ResponseModel } from '../../models/response/response.model';
import { pickupAndDeliveryKm } from '../../models/pickup-and-delivery/pickupAndDeliveryKm.model';
import { FordVehicleRequestModel } from '../../models/ford-user-with-vehicles-models/ford-vehicle-request.model';
import { REGION_C } from '../../../utils/constants';
import { FordProtect } from '../../models/ford-user-with-vehicles-models/fordProtect.model';
import { FordvehicleDetailModel } from '../../models/ford-user-with-vehicles-models/fordVehicleDetail.model';
import { VehicleInfoModel } from '../../models/vehicle/vehicle-info.model';
import { IVehicleDataTMC } from '@core/interface';
import { GetCountryService } from '@core/services';

const TMC_REQUEST_TIMEOUT_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class VehicleService {
  storage!: Storage;
  dataTMC = signal<IVehicleDataTMC | null>(null);
  readonly #getCountryService = inject(GetCountryService);

  constructor(
    private http: HttpClient,
  ) { }

  getMinMaxPickupAndDeliveryKm(
    country: string
  ): Observable<ResponseModel<pickupAndDeliveryKm>> {
    const api = environment.getRoute(API.dealer);
    const url = `${api}/services/pickup-delivery/kilometers?country=${country}`;

    return this.http.get<ResponseModel<pickupAndDeliveryKm>>(url);
  }

  updateVehicle(
    vin: string,
    request: FordVehicleRequestModel,
    regionC = REGION_C.SA
  ): Observable<ResponseModel<any>> {
    const headers = new HttpHeaders({ vin });
    const api = environment.getRoute(API.users);
    const url = `${api}/vehicles/user/garage?region=${regionC}`;

    return this.http.patch<ResponseModel<any>>(url, request, { headers});
  }

  getImgCar(chassis: string): Observable<ResponseModel<any>> {
    const api = environment.getRoute(API.owners);
    const url = `${api}/vehicles/${chassis}/images?country=${this.#getCountryService.getCountry()}`;

    return this.http.get<ResponseModel<any>>(url);
  }

  getVehicleDataByChassisV2(chassis: string): Observable<ResponseModel<any>> {
    const api = environment.getRoute(API.owners);
    const url = `${api}/vehicles/${chassis}?country=${this.#getCountryService.getCountry()}`;

    return this.http.get<ResponseModel<any>>(url);
  }

  getVehiclePlateByVin(vin: string, country: string): Observable<ResponseModel<VehicleInfoModel>> {
    const api = environment.getRoute(API.transformation);
    const url = `${api}/vehicles/v2/chassis`;
    const headers = new HttpHeaders({ country });

    return this.http.post<ResponseModel<VehicleInfoModel>>(url, { vin }, { headers });
  }

  getTMC(vin: string): Observable<IVehicleDataTMC | null> {
    const api = environment.getRoute(API.transformation);
    const url = `${api}/tmc/v2/tmc-by-vin`;

    return this.http.post<IVehicleDataTMC>(url, { vin }).pipe(
      timeout(TMC_REQUEST_TIMEOUT_MS),
      tap((response) => this.dataTMC.set(response)),
      catchError((error) => {
        console.error('Error loading vehicle telemetry data:', error);
        this.dataTMC.set(null);
        return of(null);
      })
    );
  }

  // getVehicleDataByChassisBff(chassis: string): Observable<ResponseModel<any>> {
  //   const url = environment.osbBff.concat(
  //     `/vehicles/${chassis}?country=${this.#getCountryService.getCountry()}`
  //   );
  //   return this.http.get<ResponseModel<any>>(url, {
  //     headers: this.authService.getHeadersForBff(),
  //   });
  // }

  getFordProtect(vin: string): Observable<ResponseModel<FordProtect>> {
    const url = `${environment.getRoute(API.owners)}/vehicles/v2/ford-protect`;
    const headers = new HttpHeaders({country: this.#getCountryService.getCountry() });
    const param = { vin };
    return this.http.post<ResponseModel<FordProtect>>(url,  param, { headers });
  }

  // getFordProtectBff(vin: string): Observable<ResponseModel<FordProtect>> {
  //   const url: string = environment.osbBff.concat(
  //     `/vehicles/${vin}/protect?country=${this.#getCountryService.getCountry()}`
  //   );

  //   return this.http.get<ResponseModel<FordProtect>>(url, {
  //     headers: this.authService.getHeadersForBff(),
  //   });
  // }

  getVehicleDetailByVin(
    vin: string,
    country: string
  ): Observable<FordvehicleDetailModel> {
    const api = environment.getRoute(API.owners);
    const url = `${api}/vehicles/details`;
    const normalizedVin = vin.toUpperCase();
    const headers = new HttpHeaders({ country, vin: normalizedVin });

    if (localStorage.getItem('@Ford-vehicleData')) {
      return new Observable((observer) => {
        observer.next(JSON.parse(localStorage.getItem('@Ford-vehicleData') || '{}'));
        observer.complete();
      });
    }

    return this.http.get<ResponseModel<FordvehicleDetailModel>>(url, {headers}).pipe(
      map((data) => {
        const vehicleData = data.response;
        localStorage.setItem('@Ford-vehicleData', JSON.stringify(vehicleData));
        return vehicleData;
      })
    );
  }

  // getVehicleDetailByVinBff(
  //   vin: string,
  //   country: string
  // ): Observable<ResponseModel<FordvehicleDetailModel>> {
  //   const url = environment.osbBff.concat(
  //     `/vehicles/${vin.toUpperCase()}/${country}/details`
  //   );

  //   const headers = this.authService.getHeadersForBff();

  //   return this.http.get<ResponseModel<FordvehicleDetailModel>>(url, {
  //     headers,
  //   });
  // }

  getIsCommercialVehicle(vin: string, country: string): Observable<number> {
    const api = environment.getRoute(API.transformation);
    const url = `${api}/vehicles/v2/commercial`;
    const headers = new HttpHeaders({ country });
    return this.http.post<number>(url, { vin }, { headers });
  }

  getIsElectricalVehicle(vin: string, country: string): Observable<number> {
    const api = environment.getRoute(API.transformation);
    const url = `${api}/vehicles/${vin}/electrical?country=${country}`;

    return this.http.get<number>(url);
  }
}
